import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, Clock, Check, X, Sparkles, Lightbulb, ArrowRight, Mic, PenLine, BookOpen, Headphones } from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Seo from '@/components/shared/Seo';
import { QuestionPassage, QuestionStem, AnswerOptionList, EXAM_TEXT_CLS } from '@/components/exam/QuestionDisplay';
import IeltsReadingExam from '@/components/exam/IeltsReadingExam';
import IeltsListeningExam from '@/components/exam/IeltsListeningExam';
import { ieltsApi, goToIeltsAuth } from '@/lib/ieltsApi';
import { toast } from 'sonner';

const SECTIONS = [
  { key: 'listening', label: 'Listening' },
  { key: 'reading',   label: 'Reading' },
  { key: 'writing',   label: 'Writing' },
  { key: 'speaking',  label: 'Speaking' },
];
const MCQ_TYPES = new Set(['mcq']);
const TEXT_TYPES = new Set(['fill_blank', 'short_answer', 'true_false_not_given']);
const ESSAY_TYPES = new Set(['essay_prompt']);
const SPEAKING_TYPES = new Set(['speaking_prompt']);

const IeltsPracticePage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [section, setSection] = useState(SECTIONS.find((s) => s.key === params.get('section'))?.key || 'reading');
  const [module, setModule] = useState('academic');
  const [topic, setTopic] = useState(params.get('topic') || '');
  const [difficulty, setDifficulty] = useState('');
  const [timed, setTimed] = useState(true);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [question, setQuestion] = useState(null);
  const [selected, setSelected] = useState(null);
  const [textAnswer, setTextAnswer] = useState('');
  const [essayText, setEssayText] = useState('');
  const [result, setResult] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [mobile, setMobile] = useState(false);

  // Reading-passage state (real-IELTS structure)
  const [readingPassage, setReadingPassage] = useState(null);
  const [readingQuestions, setReadingQuestions] = useState([]);
  const [readingAnswers, setReadingAnswers] = useState({});
  const [readingResults, setReadingResults] = useState(null);

  // Listening-set state (real-IELTS structure)
  const [listeningSet, setListeningSet] = useState(null);
  const [listeningQuestions, setListeningQuestions] = useState([]);
  const [listeningAnswers, setListeningAnswers] = useState({});
  const [listeningResults, setListeningResults] = useState(null);

  const startedAt = useRef(null);

  // ⇢ Writing/Speaking have dedicated split-screen experiences.
  // If a user lands here with section=writing|speaking in the URL, send them there immediately.
  useEffect(() => {
    const s = params.get('section');
    if (s === 'writing') {
      const mod = params.get('module') === 'general' ? 'general' : 'academic';
      const task = params.get('task') === '2' ? 2 : 1;
      navigate(`/ielts/writing/practice?module=${mod}&task=${task}`, { replace: true });
    } else if (s === 'speaking') {
      const sp = ['part_1', 'part_2', 'part_3'].includes(params.get('part')) ? params.get('part') : 'part_1';
      navigate(`/ielts/speaking/practice?speaking_part=${sp}`, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    ieltsApi.authMe()
      .catch((e) => { if (e.status === 401) setNeedsLogin(true); })
      .finally(() => setAuthChecked(true));
  }, []);

  useEffect(() => {
    if (!question || result) return;
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [question, result]);

  // Timer for reading-passage / listening-set mode
  useEffect(() => {
    if (!(readingPassage || listeningSet) || readingResults || listeningResults) return;
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [readingPassage, listeningSet, readingResults, listeningResults]);

  const resetForNewQ = () => {
    setSelected(null); setTextAnswer(''); setEssayText('');
    setResult(null); setSeconds(0); startedAt.current = Date.now();
    setReadingPassage(null); setReadingQuestions([]);
    setReadingAnswers({}); setReadingResults(null);
    setListeningSet(null); setListeningQuestions([]);
    setListeningAnswers({}); setListeningResults(null);
  };

  // When user toggles to Writing/Speaking, clear stale legacy question/result state and dismiss any error toasts.
  useEffect(() => {
    if (section === 'writing' || section === 'speaking') {
      setQuestion(null);
      setResult(null);
      setReadingPassage(null); setReadingQuestions([]); setReadingResults(null);
      setListeningSet(null); setListeningQuestions([]); setListeningResults(null);
      toast.dismiss();
    }
    // eslint-disable-next-line
  }, [section]);

  const loadNext = async () => {
    // Writing and Speaking have their own dedicated pages with full feature sets.
    if (section === 'writing') {
      toast.dismiss();
      const mod = module === 'general' ? 'general' : 'academic';
      navigate(`/ielts/writing/practice?module=${mod}&task=1`);
      return;
    }
    if (section === 'speaking') {
      toast.dismiss();
      navigate('/ielts/speaking/practice?speaking_part=part_1');
      return;
    }
    setLoading(true);
    resetForNewQ();
    try {
      if (section === 'reading') {
        // Real-IELTS Reading: passage + grouped questions.
        const params = new URLSearchParams();
        if (module) params.set('module', module);
        if (difficulty) params.set('difficulty', difficulty);
        const qs = params.toString() ? `?${params.toString()}` : '';
        const r = await ieltsApi.readingNextPassage(qs);
        setReadingPassage(r.passage);
        setReadingQuestions(r.questions || []);
        startedAt.current = Date.now();
      } else if (section === 'listening') {
        // Real-IELTS Listening: audio + transcript + grouped questions.
        const params = new URLSearchParams();
        if (module) params.set('module', module);
        if (difficulty) params.set('difficulty', difficulty);
        const qs = params.toString() ? `?${params.toString()}` : '';
        const r = await ieltsApi.listeningNextSet(qs);
        setListeningSet(r.listening_set);
        setListeningQuestions(r.questions || []);
        startedAt.current = Date.now();
      } else {
        const r = await ieltsApi.practiceNext({ section, module, topic: topic || undefined, difficulty: difficulty || undefined, timed });
        setQuestion(r.question);
      }
    } catch (e) {
      if (e.status === 401) setNeedsLogin(true);
      else toast.error(e.message || 'Could not load question');
    } finally { setLoading(false); }
  };

  const onReadingChangeAnswer = (qid, partial) => {
    setReadingAnswers((prev) => ({ ...prev, [qid]: { ...(prev[qid] || {}), ...partial } }));
  };
  const onListeningChangeAnswer = (qid, partial) => {
    setListeningAnswers((prev) => ({ ...prev, [qid]: { ...(prev[qid] || {}), ...partial } }));
  };

  const submitReading = async () => {
    if (!readingPassage) return;
    const answers = readingQuestions.map((q) => ({
      question_id: q.id,
      selected_option: readingAnswers[q.id]?.selected_option,
      text_answer: readingAnswers[q.id]?.text_answer,
    }));
    try {
      const r = await ieltsApi.readingSubmitBatch({
        passage_id: readingPassage.passage_id,
        answers,
        seconds_spent: seconds,
      });
      setReadingResults(r);
    } catch (e) { toast.error(e.message || 'Submit failed'); }
  };

  const submitListening = async () => {
    if (!listeningSet) return;
    const answers = listeningQuestions.map((q) => ({
      question_id: q.id,
      selected_option: listeningAnswers[q.id]?.selected_option,
      text_answer: listeningAnswers[q.id]?.text_answer,
    }));
    try {
      const r = await ieltsApi.listeningSubmitBatch({
        listening_set_id: listeningSet.listening_set_id,
        answers,
        seconds_spent: seconds,
      });
      setListeningResults(r);
    } catch (e) { toast.error(e.message || 'Submit failed'); }
  };

  const submit = async () => {
    if (!question) return;
    const qt = question.question_type || 'mcq';
    const body = { question_id: question.id, seconds_spent: seconds };
    if (MCQ_TYPES.has(qt)) {
      if (!selected) return toast.info('Select an answer first');
      body.selected_option = selected;
    } else if (TEXT_TYPES.has(qt)) {
      if (!textAnswer.trim()) return toast.info('Enter your answer first');
      body.text_answer = textAnswer.trim();
    } else if (ESSAY_TYPES.has(qt)) {
      // Writing has moved to the dedicated /ielts/writing experience with AI feedback.
      toast.info('Writing practice has moved — redirecting to the new Writing AI page…');
      setTimeout(() => { window.location.assign('/ielts/writing'); }, 600);
      return;
    } else if (SPEAKING_TYPES.has(qt)) {
      try {
        await ieltsApi.speakingSubmit({ question_id: question.id, transcript: textAnswer || 'audio recording placeholder', seconds_spent: seconds });
        toast.success('Response saved.');
        await ieltsApi.practiceSubmit(body);
      } catch (e) { toast.error(e.message); return; }
      setResult({ is_correct: null, correct_answer: null, explanation: '🎤 Your speaking response has been saved. AI feedback for IELTS speaking is on our roadmap.', question_type: qt });
      return;
    }

    try {
      const r = await ieltsApi.practiceSubmit(body);
      setResult(r);
    } catch (e) { toast.error(e.message || 'Submit failed'); }
  };

  if (!authChecked) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]"><Loader2 className="h-7 w-7 animate-spin text-red-700" /></div>;
  }
  if (needsLogin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] px-4">
        <Card className="max-w-md w-full"><CardContent className="p-8 text-center">
          <div className="w-12 h-12 rounded-xl bg-black flex items-center justify-center mx-auto mb-4"><Sparkles className="h-5 w-5 text-[#D3D86F]" /></div>
          <h1 className="text-2xl font-bold mb-2">Sign in to practise</h1>
          <p className="text-sm text-gray-600 mb-6">Adaptive IELTS practice tailored to your level.</p>
          <Button onClick={() => goToIeltsAuth('/ielts/practice')} className="w-full bg-black hover:bg-gray-900 text-white rounded-full h-11">Sign in with Google</Button>
        </CardContent></Card>
      </div>
    );
  }

  const qt = question?.question_type || 'mcq';
  const inReadingMode = section === 'reading';
  const inListeningMode = section === 'listening';
  const inSetMode = inReadingMode || inListeningMode;
  return (
    <div className="min-h-screen bg-[#FAF9F6] flex flex-col" data-testid="ielts-practice">
      <Seo title="IELTS Practice · Winhouse" canonical="/ielts/practice" />
      <Navigation mobileMenuOpen={mobile} setMobileMenuOpen={setMobile} />

      <main className={`flex-1 mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-10 w-full ${inSetMode ? 'max-w-7xl' : 'max-w-3xl'}`}>
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-red-700 font-semibold mb-1.5">Adaptive Practice</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">IELTS Practice</h1>
        </div>

        <Card className="mb-5 border-gray-200">
          <CardContent className="p-4 sm:p-5 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[140px]">
              <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-1">Section</label>
              <select className="border border-gray-200 rounded-lg p-2.5 bg-white w-full text-sm"
                value={section} onChange={(e) => setSection(e.target.value)} data-testid="ielts-prac-section">
                {SECTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div className="flex-1 min-w-[120px]">
              <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-1">Module</label>
              <select className="border border-gray-200 rounded-lg p-2.5 bg-white w-full text-sm"
                value={module} onChange={(e) => setModule(e.target.value)}>
                <option value="academic">Academic</option>
                <option value="general">General Training</option>
              </select>
            </div>
            <div className="flex-1 min-w-[120px]">
              <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-1">Topic (optional)</label>
              <input className="border border-gray-200 rounded-lg p-2.5 bg-white w-full text-sm"
                value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. environment" />
            </div>
            <div className="flex-1 min-w-[120px]">
              <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-1">Difficulty</label>
              <select className="border border-gray-200 rounded-lg p-2.5 bg-white w-full text-sm"
                value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
                <option value="">Adaptive</option><option value="easy">Easy</option>
                <option value="medium">Medium</option><option value="hard">Hard</option>
              </select>
            </div>
            <label className="text-sm flex items-center gap-2 self-center text-gray-700">
              <input type="checkbox" className="accent-red-700" checked={timed} onChange={(e) => setTimed(e.target.checked)} /> Timed
            </label>
            <Button onClick={loadNext} className="ml-auto bg-red-700 hover:bg-red-800 text-white rounded-full h-11 px-5" data-testid="ielts-prac-next">
              {section === 'writing'
                ? 'Open Writing AI →'
                : section === 'speaking'
                  ? 'Open Speaking practice →'
                  : inReadingMode
                    ? (readingPassage ? 'Next passage' : 'Start practice')
                    : inListeningMode
                      ? (listeningSet ? 'Next listening set' : 'Start practice')
                      : (question ? 'Next question' : 'Start practice')
              } {section === 'writing' || section === 'speaking' ? null : <ArrowRight className="h-4 w-4 ml-1" />}
            </Button>
          </CardContent>
        </Card>

        {/* Writing → dedicated split-screen page hint */}
        {section === 'writing' && !loading && (
          <Card className="mb-6 border-red-100 bg-gradient-to-br from-red-50/60 to-amber-50/40">
            <CardContent className="p-5 sm:p-6 flex flex-wrap items-center gap-4" data-testid="ielts-prac-writing-hint">
              <div className="w-12 h-12 rounded-xl bg-black flex items-center justify-center flex-shrink-0">
                <PenLine className="h-5 w-5 text-[#D3D86F]" />
              </div>
              <div className="flex-1 min-w-[240px]">
                <h3 className="font-bold text-base mb-0.5">Writing has its own dedicated page</h3>
                <p className="text-sm text-gray-700">
                  Split-screen editor, 20/40-min timers, live word counter, autosave and instant Claude AI band feedback.
                </p>
              </div>
              <Button onClick={() => navigate(`/ielts/writing/practice?module=${module === 'general' ? 'general' : 'academic'}&task=1`)}
                className="bg-black text-white rounded-full h-11 px-5" data-testid="ielts-prac-writing-go">
                Open Writing AI <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Speaking → dedicated page hint */}
        {section === 'speaking' && !loading && (
          <Card className="mb-6 border-red-100 bg-gradient-to-br from-red-50/60 to-amber-50/40">
            <CardContent className="p-5 sm:p-6 flex flex-wrap items-center gap-4" data-testid="ielts-prac-speaking-hint">
              <div className="w-12 h-12 rounded-xl bg-black flex items-center justify-center flex-shrink-0">
                <Mic className="h-5 w-5 text-[#D3D86F]" />
              </div>
              <div className="flex-1 min-w-[240px]">
                <h3 className="font-bold text-base mb-0.5">Speaking has its own dedicated page</h3>
                <p className="text-sm text-gray-700">
                  Cue cards, prep + speaking timers, browser recording, typed transcript fallback, and instant Claude AI band feedback.
                </p>
              </div>
              <Button onClick={() => navigate('/ielts/speaking/practice?speaking_part=part_1')}
                className="bg-black text-white rounded-full h-11 px-5" data-testid="ielts-prac-speaking-go">
                Open Speaking AI <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}

        {loading && <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-red-700" /></div>}

        {/* ── Real-IELTS Reading: passage + grouped questions ────────────── */}
        {inReadingMode && readingPassage && !loading && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-red-700" />
                <span className="text-sm font-semibold text-gray-800">IELTS Reading practice</span>
                <span className="text-xs text-gray-500">· {readingQuestions.length} question{readingQuestions.length === 1 ? '' : 's'}</span>
              </div>
              {timed && (
                <span className="inline-flex items-center gap-1.5 text-sm font-mono text-gray-700 bg-gray-100 rounded-full px-3 py-1" data-testid="ielts-prac-reading-timer">
                  <Clock className="h-3.5 w-3.5" /> {Math.floor(seconds/60).toString().padStart(2,'0')}:{(seconds%60).toString().padStart(2,'0')}
                </span>
              )}
            </div>
            <IeltsReadingExam
              passage={readingPassage}
              questions={readingQuestions}
              answers={readingAnswers}
              results={readingResults}
              onChangeAnswer={onReadingChangeAnswer}
              locked={!!readingResults}
            />
            <div className="mt-5 flex flex-wrap gap-2">
              {!readingResults ? (
                <Button onClick={submitReading}
                  className="bg-black hover:bg-gray-900 text-white rounded-full h-11 px-6"
                  data-testid="ielts-prac-reading-submit">
                  Submit Answers
                </Button>
              ) : (
                <>
                  <Badge className="bg-red-700 text-white hover:bg-red-700 self-center text-base px-4 py-1.5" data-testid="ielts-prac-reading-score">
                    Score: {readingResults.correct} / {readingResults.graded} · {readingResults.percent_correct}%
                  </Badge>
                  <Button onClick={loadNext}
                    className="bg-red-700 hover:bg-red-800 text-white rounded-full h-11 px-6 ml-auto">
                    Next passage <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Real-IELTS Listening: audio + transcript + grouped questions ── */}
        {inListeningMode && listeningSet && !loading && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Headphones className="h-4 w-4 text-red-700" />
                <span className="text-sm font-semibold text-gray-800">IELTS Listening practice</span>
                <span className="text-xs text-gray-500">· {listeningQuestions.length} question{listeningQuestions.length === 1 ? '' : 's'}</span>
              </div>
              {timed && (
                <span className="inline-flex items-center gap-1.5 text-sm font-mono text-gray-700 bg-gray-100 rounded-full px-3 py-1" data-testid="ielts-prac-listening-timer">
                  <Clock className="h-3.5 w-3.5" /> {Math.floor(seconds/60).toString().padStart(2,'0')}:{(seconds%60).toString().padStart(2,'0')}
                </span>
              )}
            </div>
            <IeltsListeningExam
              listeningSet={listeningSet}
              questions={listeningQuestions}
              answers={listeningAnswers}
              results={listeningResults}
              onChangeAnswer={onListeningChangeAnswer}
              locked={!!listeningResults}
            />
            <div className="mt-5 flex flex-wrap gap-2">
              {!listeningResults ? (
                <Button onClick={submitListening}
                  className="bg-black hover:bg-gray-900 text-white rounded-full h-11 px-6"
                  data-testid="ielts-prac-listening-submit">
                  Submit Answers
                </Button>
              ) : (
                <>
                  <Badge className="bg-red-700 text-white hover:bg-red-700 self-center text-base px-4 py-1.5" data-testid="ielts-prac-listening-score">
                    Score: {listeningResults.correct} / {listeningResults.graded} · {listeningResults.percent_correct}%
                  </Badge>
                  <Button onClick={loadNext}
                    className="bg-red-700 hover:bg-red-800 text-white rounded-full h-11 px-6 ml-auto">
                    Next listening set <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {!inSetMode && section !== 'writing' && section !== 'speaking' && question && !loading && (
          <Card key={question.id} className="border-gray-200 shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-300">
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
                <div className="flex flex-wrap gap-2">
                  <Badge className="bg-red-700 text-white capitalize hover:bg-red-700">{question.section}</Badge>
                  <Badge variant="outline" className="capitalize">{(question.question_type || '').replace(/_/g,' ')}</Badge>
                  <Badge variant="outline" className="capitalize">{question.topic.replace(/_/g,' ')}</Badge>
                  <Badge variant="outline" className="capitalize">{question.difficulty}</Badge>
                </div>
                {timed && (
                  <span className="inline-flex items-center gap-1.5 text-sm font-mono text-gray-700 bg-gray-100 rounded-full px-3 py-1">
                    <Clock className="h-3.5 w-3.5" /> {seconds}s
                  </span>
                )}
              </div>

              {question.passage_text && <QuestionPassage text={question.passage_text} />}
              {question.transcript_text && <QuestionPassage text={`Transcript:\n${question.transcript_text}`} />}
              {question.audio_url && (
                <audio controls src={question.audio_url} className="w-full mb-5" data-testid="ielts-prac-audio" />
              )}
              {question.image_url && (
                <img src={question.image_url} alt="" className="w-full max-w-md mx-auto rounded-lg mb-5 border border-gray-200" />
              )}

              <QuestionStem text={question.question_text} testId="ielts-prac-question-text" />

              {/* Input area, varies by question_type */}
              {MCQ_TYPES.has(qt) && (
                <AnswerOptionList question={question} selectedLetter={selected} result={result}
                  onSelect={(k) => setSelected(k)} testIdPrefix="ielts-prac-opt" />
              )}
              {TEXT_TYPES.has(qt) && (
                <div className="mb-6">
                  <input type="text" value={textAnswer} onChange={(e) => setTextAnswer(e.target.value)}
                    disabled={!!result} placeholder={qt === 'true_false_not_given' ? 'True / False / Not Given' : 'Type your answer'}
                    className="w-full border-2 border-gray-200 focus:border-red-700 rounded-xl p-4 text-base outline-none"
                    data-testid="ielts-prac-text-input" />
                </div>
              )}
              {ESSAY_TYPES.has(qt) && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2 text-xs text-gray-500">
                    <PenLine className="h-3.5 w-3.5" /> Write your essay below — IELTS Task 2 expects 250+ words.
                  </div>
                  <textarea value={essayText} onChange={(e) => setEssayText(e.target.value)}
                    disabled={!!result} rows={14} placeholder="Begin your essay..."
                    className={`w-full border-2 border-gray-200 focus:border-red-700 rounded-xl p-4 text-base outline-none font-serif ${EXAM_TEXT_CLS}`}
                    data-testid="ielts-prac-essay-input" />
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {essayText.trim().split(/\s+/).filter(Boolean).length} words
                  </div>
                </div>
              )}
              {SPEAKING_TYPES.has(qt) && (
                <div className="mb-6 p-5 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-center gap-2 mb-3 font-semibold text-red-900">
                    <Mic className="h-4 w-4" /> Speaking response
                  </div>
                  <p className="text-sm text-red-800 mb-3">
                    Audio recording is coming soon. For now, type a brief outline of how you'd answer this prompt —
                    your tutor (or our AI evaluator, once live) will review and give band-level feedback.
                  </p>
                  <textarea value={textAnswer} onChange={(e) => setTextAnswer(e.target.value)}
                    disabled={!!result} rows={6} placeholder="Outline your spoken response..."
                    className={`w-full border-2 border-red-200 focus:border-red-700 rounded-xl p-3 text-sm outline-none bg-white ${EXAM_TEXT_CLS}`}
                    data-testid="ielts-prac-speaking-input" />
                </div>
              )}

              {!result && (
                <Button onClick={submit}
                  className="w-full bg-black hover:bg-gray-900 text-white rounded-full h-12 text-base"
                  data-testid="ielts-prac-submit">
                  Submit Answer
                </Button>
              )}

              {result && (
                <div className="mt-2 animate-in fade-in slide-in-from-bottom-2 duration-300" data-testid="ielts-prac-explanation">
                  {result.is_correct !== null && (
                    <div className={`rounded-xl p-5 mb-5 ${result.is_correct ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        {result.is_correct ? <Check className="h-5 w-5 text-green-700" /> : <X className="h-5 w-5 text-red-700" />}
                        <span className={`font-bold ${result.is_correct ? 'text-green-800' : 'text-red-800'}`}>
                          {result.is_correct ? 'Correct' : (result.correct_answer ? `Incorrect — expected: ${result.correct_answer}` : 'Submitted')}
                        </span>
                      </div>
                    </div>
                  )}
                  {result.explanation && (
                    <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5">
                      <div className="flex items-center gap-2 mb-2">
                        <Lightbulb className="h-4 w-4 text-[#D3D86F]" />
                        <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Solution</span>
                      </div>
                      <p className={`text-sm sm:text-base text-gray-800 ${EXAM_TEXT_CLS}`}>{result.explanation}</p>
                    </div>
                  )}
                  <Button onClick={loadNext} className="w-full bg-red-700 hover:bg-red-800 text-white rounded-full h-12">
                    Next question <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {!inSetMode && section !== 'writing' && section !== 'speaking' && !question && !loading && (
          <Card className="border-dashed border-2 border-gray-200">
            <CardContent className="p-10 text-center">
              <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="h-5 w-5 text-red-700" />
              </div>
              <p className="text-gray-700 font-semibold mb-1">Ready when you are.</p>
              <p className="text-sm text-gray-500">Pick a section and tap "Start practice" to begin.</p>
            </CardContent>
          </Card>
        )}

        {inReadingMode && !readingPassage && !loading && (
          <Card className="border-dashed border-2 border-gray-200">
            <CardContent className="p-10 text-center">
              <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mx-auto mb-4">
                <BookOpen className="h-5 w-5 text-red-700" />
              </div>
              <p className="text-gray-700 font-semibold mb-1">Real IELTS Reading practice</p>
              <p className="text-sm text-gray-500 max-w-md mx-auto">
                Each session presents a full passage on the left and grouped IELTS-style questions on the right —
                True/False/Not Given, Matching Headings, Sentence Completion, and more.
              </p>
            </CardContent>
          </Card>
        )}

        {inListeningMode && !listeningSet && !loading && (
          <Card className="border-dashed border-2 border-gray-200">
            <CardContent className="p-10 text-center">
              <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mx-auto mb-4">
                <Headphones className="h-5 w-5 text-red-700" />
              </div>
              <p className="text-gray-700 font-semibold mb-1">Real IELTS Listening practice</p>
              <p className="text-sm text-gray-500 max-w-md mx-auto">
                Each session presents an audio recording + transcript on the left and IELTS-style grouped questions on the right —
                Note Completion, Multiple Choice, Map Labeling, Matching, and more.
              </p>
            </CardContent>
          </Card>
        )}
      </main>
      <Footer />
    </div>
  );
};
export default IeltsPracticePage;
